// Internal areas: Training Lab, Models, Engineering, Security, Operations. Sample data until each is connected.
import { BehaviorTotalsCard, ScoreCard } from "../components/ScoreCard";
import { useState } from "react";
import { Head, Icon, Locked, Pill, useStudio } from "../components/ui";
import { TRAINING_QUEUE } from "../data/sample";
import { AzureLive, GitHubLive, useGitHubLive } from "../components/Live";
import { TrainingLabLive } from "../components/TrainingLab";
import { CompanyOperations, CostsByProject } from "../components/Costs";
import { vera } from "../api/vera";
import { AskVera } from "../components/AskVera";

export function Training() {
  const { profile } = useStudio();
  // Live: the real Training Lab, for Eva and the psychologist (reviewTraining). Otherwise the sample below.
  if (vera.mode === "live" && profile.reviewTraining) return <TrainingLabLive />;
  return <TrainingSample />;
}

function TrainingSample() {
  const { profile, toast } = useStudio();
  const [queue, setQueue] = useState(TRAINING_QUEUE.map((x) => ({ ...x, state: "waiting" as "waiting" | "approved" | "repair" })));
  const waiting = queue.filter((x) => x.state === "waiting").length;
  const decide = (id: string, state: "approved" | "repair") => {
    setQueue((q) => q.map((x) => (x.id === id ? { ...x, state } : x)));
    toast(state === "approved" ? "Approved. It joins the next training set." : "Sent back for repair with your note.");
  };
  return (
    <>
      <Head title="Training Lab" lead="Where VERA's mind is shaped. Every example is reviewed by Monika before it reaches training, and the crisis protocol is checked on every build.">
        <button className="btn" onClick={() => toast("Compiles approved examples into a training file once connected to the dataset store.")}>Compile training set</button>
      </Head>
      <div className="grid g4">
        <div className="card stat"><b className="num">74</b><span>Approved examples</span></div>
        <div className="card stat"><b className="num">{waiting}</b><span>Waiting for review</span></div>
        <div className="card stat"><b className="num">8/8</b><span>Crisis examples pass</span></div>
        <div className="card stat"><b className="num">23</b><span>Patterns in the atlas</span></div>
      </div>
      <div className="split">
        <div className="card"><h2 style={{ marginBottom: 6 }}>Review queue</h2>
          <div className="list">{queue.map((x) => (
            <div className="li" key={x.id}><span className="ico"><Icon name={x.tag === "Crisis protocol" ? "heart" : "training"} /></span>
              <div className="grow"><b>{x.title}</b><div className="faint" style={{ fontSize: 12 }}>{x.tag} · from {x.by}</div></div>
              {x.state === "waiting" ? (profile.reviewTraining
                ? <div className="row"><button className="btn primary" onClick={() => decide(x.id, "approved")}>Approve</button><button className="btn" onClick={() => decide(x.id, "repair")}>Repair</button></div>
                : <Pill tone="warn">Waiting</Pill>) : <Pill tone={x.state === "approved" ? "ok" : "warn"}>{x.state === "approved" ? "Approved" : "Sent back"}</Pill>}
            </div>))}</div></div>
        <div className="card stack"><h2>Patterns</h2><p className="mute">The behaviours VERA learns to notice.</p>
          <div className="row">{["On edge", "Shut down", "Overload", "Avoidance", "Rushed decisions", "Freeze", "Conflict", "Burnout", "Steady"].map((p) => <span className="chip" key={p}>{p}</span>)}</div>
          <div className="notice calm"><span className="mute">VERA regulates first, then solves. Examples that solve before settling go back for repair.</span></div></div>
      </div>
    </>
  );
}

// The three VERA engine plans. Each organisation (VERANeural itself, or a client) runs on one; people only ever see "VERA".
export const ENGINE_PLANS = [
  { id: "core", name: "VERA Core", tag: "Most private", models: "VERA's own fine-tuned models, plus Azure services (Maps, Document Intelligence, Search)",
    bestFor: "Regulated teams and anyone who wants their work to stay with VERA's own models", data: "Stays in VERANeural's Azure" },
  { id: "azure", name: "VERA + Azure", tag: "Recommended", models: "Everything in Core, plus Azure OpenAI models in VERANeural's Azure AI Foundry: deep reasoning, vision and real-time voice",
    bestFor: "Most teams: live voice, images, long documents and deeper answers", data: "Stays in VERANeural's Azure, never used for training" },
  { id: "frontier", name: "VERA Frontier", tag: "Most capable", models: "Everything in VERA + Azure, plus approved frontier models (such as Anthropic and OpenAI), picked by VERA's orchestrator per task",
    bestFor: "Engineering, heavy coding and the hardest analysis", data: "Only providers under contract with no training on your data; every call logged" },
] as const;

function EnginePlans() {
  const { profile, toast } = useStudio();
  const [plan, setPlan] = useState<string>("azure");
  const admin = profile.models === "full";
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 19 }}>VERA engine</h2><span className="faint" style={{ fontSize: 12.5 }}>Set per organisation. Everyone just sees VERA.</span></div>
      <div className="plans">
        {ENGINE_PLANS.map((p) => (
          <button key={p.id} className={`plan ${plan === p.id ? "on" : ""}`} disabled={!admin} aria-pressed={plan === p.id}
            onClick={() => { setPlan(p.id); toast(`${p.name} selected. VERA's control plane applies it once connected.`); }}>
            <span className="row" style={{ justifyContent: "space-between" }}><b>{p.name}</b><Pill tone={p.id === "azure" ? "ok" : "info"}>{p.tag}</Pill></span>
            <span className="pm">{p.models}</span>
            <span className="pb"><b>Best for</b> {p.bestFor}</span>
            <span className="pb"><b>Your data</b> {p.data}</span>
            <span className="faint" style={{ fontSize: 12 }}>Price to be set</span>
          </button>))}
      </div>
      {!admin && <p className="faint" style={{ fontSize: 12.5 }}>Only the founders can change the engine.</p>}
    </div>
  );
}

export function Models() {
  const { profile, toast } = useStudio();
  const rows = [["VERA Core (fine-tuned)", "Azure AI Foundry", "ok", "Healthy", "1.4s", "Primary"], ["Reasoning model", "Azure AI Foundry", "ok", "Healthy", "2.1s", "Deep work"], ["Router (orchestrator)", "Azure AI Foundry", "ok", "Healthy", "0.3s", "Picks specialists"], ["Realtime voice", "Azure AI Foundry", "warn", "Slow", "820ms", "Voice"], ["vera-core (local)", "Ollama", "info", "Local only", "3.8s", "Offline work"]];
  return (
    <>
      <Head title="Models" lead="The engine behind VERA. Every model is reached only through VERA's control plane, within the plan each organisation chooses.">
        {profile.models === "full" ? <button className="btn primary" onClick={() => toast("Runs the evaluation set once connected to the control plane.")}>Run benchmark</button> : <Locked />}
      </Head>
      <EnginePlans />
      <div className="card tbl-wrap"><table><thead><tr><th>Model</th><th>Where</th><th>Health</th><th>Response</th><th>Used for</th></tr></thead><tbody>
        {rows.map((r) => <tr key={r[0]}><td><b>{r[0]}</b></td><td className="faint">{r[1]}</td><td><Pill tone={r[2]}>{r[3]}</Pill></td><td className="num">{r[4]}</td><td>{r[5]}</td></tr>)}
      </tbody></table></div>
      <p className="faint" style={{ fontSize: 12.5 }}>Sample data. Live health comes from the control plane's models/health endpoint.</p>
    </>
  );
}

export function Engineering() {
  const { profile, go } = useStudio();
  const gh = useGitHubLive();
  return (
    <>
      <Head title="Engineering" lead="Repositories, pull requests and builds. VERA can prepare changes, but every change arrives as a pull request a person reviews.">
        {profile.engineering === "full" ? <button className="btn primary" onClick={() => go("workspace", { project: "veraai" })}>Build with VERA</button> : <Locked />}
      </Head>
      {gh.state === "connected" ? <GitHubLive live={gh} /> : (
        <div className="card stack" style={{ alignItems: "flex-start", gap: 10 }}>
          <h2>Connect GitHub</h2>
          <p className="mute">See VeraNeural's repositories, open pull requests and their checks here, read-only.</p>
          <button className="btn primary" onClick={() => go("connections")}>Open Connections</button>
        </div>
      )}
    </>
  );
}

export function Security() {
  const { toast } = useStudio();
  const people = [["Founders and executives", "Owner · Reader + Cost", "Internal"], ["Operations", "Reader + Cost + Monitoring", "Internal"], ["Engineering", "Contributor (dev) · Reader (prod)", "Internal"], ["Security", "Security Admin + Key Vault", "Internal"], ["Clients and investors", "Studio only · own workspace", "Guest"]];
  return (
    <>
      <Head title="Security" lead="Who can reach what, what AI Guard found, and every sensitive action, in one place.">
        <button className="btn primary" onClick={() => toast("Access review: each owner gets a confirmation request.")}>Start access review</button>
      </Head>
      <div className="split">
        <div className="card"><h2 style={{ marginBottom: 6 }}>Access by role</h2><div className="tbl-wrap"><table><thead><tr><th>Group</th><th>Access</th><th>Type</th></tr></thead><tbody>
          {people.map((p) => <tr key={p[0]}><td><b>{p[0]}</b></td><td className="faint">{p[1]}</td><td><Pill tone={p[2] === "Guest" ? "warn" : "info"}>{p[2]}</Pill></td></tr>)}
        </tbody></table></div></div>
        <div className="card"><h2 style={{ marginBottom: 6 }}>Before the team gets access</h2>
          <div className="list">{["Limit activity history to each person's own workspaces", "Limit customer build to approved roles", "Hide workspace names from the public health check", "Move sign-in tokens to Key Vault"].map((g) => <div className="li" key={g}><Pill tone="warn">Open</Pill><div className="grow">{g}</div></div>)}</div></div>
      </div>
    </>
  );
}

export function Operations() {
  const { profile } = useStudio();
  // A customer's Operations is its own costs; the team's is every project's.
  if (profile.external) return <CompanyOperations />;
  return (
    <>
      <Head title="Operations" lead="Spend, usage, health and the team, for the monthly operations pack. Read-only by design.">
        <AskVera heading="Operations" where="Operations for VERANeural: costs per project, VERA's work, Azure resources and the team"
          ideas={["Which project cost the most this month, and why?", "Where can we save?", "What did VERA do most this month?"]} />
      </Head>
      <ScoreCard />
      {profile.key === "founder" && <BehaviorTotalsCard />}
      <CostsByProject />
      <AzureLive />
    </>
  );
}
